import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { newsletterActions } from "../slices/newsletter";
import { getErrorMessage } from "../../utils/errorUtils";
import { NewsletterRequestDto } from "../../shared/dtos/newsletter/NewsletterRequestDto";
import { NewsletterResponseDto } from "../../shared/dtos/newsletter/NewsletterResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { NewsletterAudience } from "../../shared/enums/NewsletterAudience";

export interface NewsletterQueryParams {
  branchId?: string;
  academicYearId?: string;
  termId?: string;
  gradeLevelId?: string;
  userId?: string;
  audienceScope?: NewsletterAudience;
  search?: string;
  page?: number;
  size?: number;
}

export const getNewsletters =
  (params: NewsletterQueryParams = { page: 1, size: 12 }) =>
  async (dispatch: AppDispatch) => {
    dispatch(newsletterActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<NewsletterResponseDto>>
      >("/newsletters", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(newsletterActions.setItems(data.data.content));
        dispatch(newsletterActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(newsletterActions.setErrorFetchAll(message));
    }
  };

export const createNewsletter =
  (values: NewsletterRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(newsletterActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<NewsletterResponseDto>
      >("/newsletters", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Newsletter created successfully!");
        dispatch(newsletterActions.createSuccess(data.data));
        dispatch(
          newsletterActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(newsletterActions.setErrorCreate(message));
    }
  };

export const getNewsletterById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(newsletterActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<NewsletterResponseDto>
      >(`/newsletters/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(newsletterActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(newsletterActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateNewsletter =
  (payload: { id: string; values: NewsletterRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(newsletterActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<NewsletterResponseDto>
      >(`/newsletters/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Newsletter updated successfully!");
        dispatch(newsletterActions.updateSuccess(data.data));
        dispatch(
          newsletterActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(newsletterActions.setErrorUpdate(message));
    }
  };

export const deleteNewsletter =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(newsletterActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/newsletters/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Newsletter deleted successfully!");
        dispatch(newsletterActions.deleteSuccess(id));
        dispatch(
          newsletterActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(newsletterActions.setErrorDelete(message));
    }
  };

export const resetNewsletterServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(newsletterActions.setServerStatus(null));
};
